import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Briefcase, Download, Search, ArrowUpDown } from 'lucide-react';
import * as XLSX from 'xlsx';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReporteTrabajos = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const estadoFiltro = searchParams.get('estado');
  
  const [trabajos, setTrabajos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('fecha_creacion');
  const [sortDirection, setSortDirection] = useState('desc');

  useEffect(() => {
    cargarTrabajos();
  }, [estadoFiltro]);

  const cargarTrabajos = async () => {
    try {
      let url = `${API}/trabajos`;
      if (estadoFiltro) {
        url += `?estado=${estadoFiltro}`;
      }
      
      const response = await axios.get(url);
      setTrabajos(response.data);
    } catch (err) {
      console.error('Error al cargar trabajos:', err);
    } finally {
      setLoading(false);
    }
  };

  const exportarExcel = () => {
    const data = trabajos.map(t => ({
      'Folio': t.folio || 'N/A',
      'Título': t.titulo,
      'Descripción': t.descripcion,
      'Cliente': t.cliente_nombre || 'N/A',
      'Lugar': t.lugar_nombre || 'N/A',
      'Ciudad': t.ciudad_nombre || 'N/A',
      'Técnico': t.tecnico_asignado_nombre || 'N/A',
      'Estado': formatEstado(t.estado),
      'Fecha Creación': new Date(t.fecha_creacion).toLocaleDateString('es-ES'),
      'Fecha Finalización': t.fecha_finalizacion ? new Date(t.fecha_finalizacion).toLocaleDateString('es-ES') : 'N/A',
      'Horas Trabajadas': t.horas_trabajadas || 'N/A',
      'Dirección': t.direccion || 'N/A'
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Trabajos');
    
    const filename = `trabajos_${estadoFiltro || 'todos'}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, filename);
  };

  const formatEstado = (estado) => {
    const estados = {
      'pendiente': 'Pendiente',
      'en_progreso': 'En Progreso',
      'completado': 'Completado'
    };
    return estados[estado] || estado;
  };

  const getEstadoColor = (estado) => {
    const colores = {
      'pendiente': 'bg-yellow-100 text-yellow-800',
      'en_progreso': 'bg-blue-100 text-blue-800',
      'completado': 'bg-green-100 text-green-800'
    };
    return colores[estado] || 'bg-gray-100 text-gray-800';
  };

  const getTitulo = () => {
    if (estadoFiltro) {
      return `Trabajos ${formatEstado(estadoFiltro)}`;
    }
    return 'Todos los Trabajos';
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const filteredAndSortedTrabajos = trabajos
    .filter(trabajo =>
      trabajo.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (trabajo.descripcion || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (trabajo.cliente_nombre || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (trabajo.tecnico_asignado_nombre || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (trabajo.lugar_nombre || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (trabajo.ciudad_nombre || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (trabajo.folio || '').toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      // Handle dates
      if (sortField === 'fecha_creacion') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      } else {
        aValue = aValue || '';
        bValue = bValue || '';
      }

      if (typeof aValue === 'string') {
        const comparison = aValue.localeCompare(bValue);
        return sortDirection === 'asc' ? comparison : -comparison;
      } else {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }
    });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Volver
        </Button>

        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <CardTitle className="text-2xl flex items-center gap-2">
                <Briefcase className="h-6 w-6" />
                {getTitulo()} ({filteredAndSortedTrabajos.length})
              </CardTitle>
              <Button onClick={exportarExcel} variant="outline">
                <Download className="mr-2 h-4 w-4" />
                Exportar Excel
              </Button>
            </div>
            <div className="mt-4 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Buscar por folio, trabajo, cliente, técnico, lugar o ciudad..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </CardHeader>
          <CardContent>
            {filteredAndSortedTrabajos.length === 0 ? (
              <p className="text-center text-gray-500 py-8">
                {searchTerm ? 'No se encontraron trabajos' : 'No hay trabajos para mostrar'}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th 
                        className="text-left p-3 font-semibold cursor-pointer hover:bg-gray-50"
                        onClick={() => handleSort('folio')}
                      >
                        <div className="flex items-center gap-2">
                          Folio
                          <ArrowUpDown className="h-4 w-4" />
                        </div>
                      </th>
                      <th 
                        className="text-left p-3 font-semibold cursor-pointer hover:bg-gray-50"
                        onClick={() => handleSort('titulo')}
                      >
                        <div className="flex items-center gap-2">
                          Trabajo
                          <ArrowUpDown className="h-4 w-4" />
                        </div>
                      </th>
                      <th 
                        className="text-left p-3 font-semibold cursor-pointer hover:bg-gray-50"
                        onClick={() => handleSort('cliente_nombre')}
                      >
                        <div className="flex items-center gap-2">
                          Cliente
                          <ArrowUpDown className="h-4 w-4" />
                        </div>
                      </th>
                      <th 
                        className="text-left p-3 font-semibold cursor-pointer hover:bg-gray-50"
                        onClick={() => handleSort('lugar_nombre')}
                      >
                        <div className="flex items-center gap-2">
                          Lugar
                          <ArrowUpDown className="h-4 w-4" />
                        </div>
                      </th>
                      <th 
                        className="text-left p-3 font-semibold cursor-pointer hover:bg-gray-50"
                        onClick={() => handleSort('ciudad_nombre')}
                      >
                        <div className="flex items-center gap-2">
                          Ciudad
                          <ArrowUpDown className="h-4 w-4" />
                        </div>
                      </th>
                      <th 
                        className="text-left p-3 font-semibold cursor-pointer hover:bg-gray-50"
                        onClick={() => handleSort('tecnico_asignado_nombre')}
                      >
                        <div className="flex items-center gap-2">
                          Técnico
                          <ArrowUpDown className="h-4 w-4" />
                        </div>
                      </th>
                      <th 
                        className="text-left p-3 font-semibold cursor-pointer hover:bg-gray-50"
                        onClick={() => handleSort('estado')}
                      >
                        <div className="flex items-center gap-2">
                          Estado
                          <ArrowUpDown className="h-4 w-4" />
                        </div>
                      </th>
                      <th 
                        className="text-left p-3 font-semibold cursor-pointer hover:bg-gray-50"
                        onClick={() => handleSort('fecha_creacion')}
                      >
                        <div className="flex items-center gap-2">
                          Fecha Creación
                          <ArrowUpDown className="h-4 w-4" />
                        </div>
                      </th>
                      <th className="text-left p-3 font-semibold">Horas</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAndSortedTrabajos.map((trabajo) => (
                      <tr
                        key={trabajo.id}
                        className="border-b hover:bg-gray-50 cursor-pointer"
                        onClick={() => navigate(`/admin/trabajo/${trabajo.id}`)}
                      >
                        <td className="p-3 font-mono text-sm">{trabajo.folio || 'N/A'}</td>
                        <td className="p-3">
                          <div className="font-medium">{trabajo.titulo}</div>
                          <div className="text-sm text-gray-500 line-clamp-1">{trabajo.descripcion}</div>
                        </td>
                        <td className="p-3 text-sm">{trabajo.cliente_nombre || 'N/A'}</td>
                        <td className="p-3 text-sm">{trabajo.lugar_nombre || '-'}</td>
                        <td className="p-3 text-sm">{trabajo.ciudad_nombre || '-'}</td>
                        <td className="p-3 text-sm">{trabajo.tecnico_asignado_nombre || 'N/A'}</td>
                        <td className="p-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getEstadoColor(trabajo.estado)}`}>
                            {formatEstado(trabajo.estado)}
                          </span>
                        </td>
                        <td className="p-3 text-sm">
                          {new Date(trabajo.fecha_creacion).toLocaleDateString('es-ES')}
                        </td>
                        <td className="p-3 text-sm">{trabajo.horas_trabajadas || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ReporteTrabajos;
